import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BuyWithNewQuizBtnText, Subscription } from '../../Constants';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectCurrentLanguage } from '../../features/langSlice';
import { toast } from 'react-toastify';
import DashboardWrapper from './components/DashboardWrapper';
import SubscriptionsStatusTable from './components/SubscriptionsStatusTable';
import ManageSubscriptionPanel from './components/ManageSubscriptionPanel';
import DashboardFormulaEditor from './dashboard/DashboardFormulaEditor';
import PauseSubscriptionModal from './components/PauseSubscriptionModal';
import CancelSubscriptionModal from './components/CancelSubscriptionModal';
import ResumeSubscriptionModal from './components/ResumeSubscriptionModal';
import ChangePlanModal from './components/ChangePlanModal';
import ExplainerVideo from './components/ExplainerVideo';
import { getBrowserLocale, normalizeLanguageCode } from '../../utils/language';
import {
  useGetUserSubscriptionQuery,
  useGetDashboardFormulaQuery,
  useCancelSubscriptionMutation,
  usePauseSubscriptionMutation,
  useResumeSubscriptionMutation,
  useRemoveSubscriptionItemMutation,
} from '../../features/customerApiSlice';
import { useNavigate } from 'react-router-dom';

// Recorded in German only, so it is hidden for other languages.
const SUBS_VIDEO_DE = 'https://d1ypa02v8rscne.cloudfront.net/resources/videos/philipp-erklaervideo-3-kuendigung-de%20(1).mp4';

function parseDateValue(value) {
  if (!value) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    const [year, month, day] = String(value)
      .split('-')
      .map((part) => parseInt(part, 10));
    return new Date(year, month - 1, day, 12, 0, 0, 0);
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toDateInputValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(date, days) {
  const nextDate = new Date(date.getTime());
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

function formatDisplayDate(date, locale = 'en-GB') {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function getCadenceDaysForSubscription(subscription) {
  const productType = String(subscription?.product_type || '').toLowerCase();
  const duration = parseInt(String(subscription?.duration || ''), 10);

  if (productType === 'omega-3') {
    if (duration === 1) return 30;
    if (duration === 3) return 90;
    return null;
  }

  if (duration === 3) return 30;
  if (duration === 6) return 90;
  return null;
}

function normalizeSubscriptionStatus(value) {
  return String(value || '').trim().toLowerCase();
}

function getEffectiveSubscriptionStatus(subscription) {
  return normalizeSubscriptionStatus(subscription?.stripeData?.status || subscription?.status);
}

function isOneOffSubscription(subscription) {
  const duration = parseInt(String(subscription?.duration ?? ''), 10);
  return [30, 90].includes(duration);
}

function isCancelledSubscription(subscription) {
  const localStatus = normalizeSubscriptionStatus(subscription?.status);
  const stripeStatus = normalizeSubscriptionStatus(subscription?.stripeData?.status);
  return ['cancelled', 'canceled'].includes(localStatus) || ['cancelled', 'canceled'].includes(stripeStatus);
}

function isPausedSubscription(subscription) {
  const localStatus = normalizeSubscriptionStatus(subscription?.status);
  const stripeStatus = normalizeSubscriptionStatus(subscription?.stripeData?.status);
  return (
    ['paused', 'trialing'].includes(localStatus) ||
    ['paused', 'trialing'].includes(stripeStatus) ||
    Boolean(subscription?.stripeData?.pause_collection)
  );
}

function CustomerSubscriptions() {
  const navigate = useNavigate();
  const selectedLang = useSelector(selectCurrentLanguage);
  const { t } = useTranslation();
  const [lastQuizId] = useState(() => localStorage.getItem('quiz_id'));
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showDeliveryDateModal, setShowDeliveryDateModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showResumeConfirm, setShowResumeConfirm] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [isFormulaEditorOpen, setIsFormulaEditorOpen] = useState(false);
  const lang = normalizeLanguageCode(selectedLang || localStorage.getItem('lang') || 'en');
  const hasCustomerToken = Boolean(localStorage.getItem('customer_token'));

  const [selectedSub, setSelectedSub] = useState(null);
  const managePanelRef = useRef(null);

  const { data: userSubs, isLoading, refetch: refetchUserSubscription } = useGetUserSubscriptionQuery();

  const subscriptionsList = useMemo(() => {
    if (Array.isArray(userSubs)) return userSubs;
    return userSubs?.subscription ?? [];
  }, [userSubs]);

  const visibleSubscriptionsList = useMemo(() => {
    if (!Array.isArray(subscriptionsList) || subscriptionsList.length === 0) {
      return [];
    }
    const byId = new Map(subscriptionsList.map((sub) => [String(sub.id), sub]));
    return subscriptionsList.filter((sub) => {
      const status = String(sub?.status || '').toLowerCase();
      const upgradedToId = sub?.upgrade_usersubid ? String(sub.upgrade_usersubid) : null;
      if (status === 'upgraded' && upgradedToId && byId.has(upgradedToId)) {
        return false;
      }
      return true;
    });
  }, [subscriptionsList]);

  const [cancelSubscriptionMutation, { isLoading: isCancelling }] = useCancelSubscriptionMutation();
  const [pauseSubscriptionMutation, { isLoading: isPausing }] = usePauseSubscriptionMutation();
  const [resumeSubscriptionMutation, { isLoading: isResuming }] = useResumeSubscriptionMutation();
  const [removeSubscriptionItemMutation, { isLoading: isRemoving }] = useRemoveSubscriptionItemMutation();

  const { user } = useSelector((state) => state.user);

  // Keep selectedSub in sync after refetches
  useEffect(() => {
    if (selectedSub && visibleSubscriptionsList.length > 0) {
      const updated = visibleSubscriptionsList.find((s) => s.id === selectedSub.id);
      if (updated && updated !== selectedSub) {
        setSelectedSub(updated);
      } else if (!updated) {
        setSelectedSub(null);
      }
    }
  }, [visibleSubscriptionsList, selectedSub]);

  useEffect(() => {
    if (!selectedSub || !managePanelRef.current) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      managePanelRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 120);

    return () => window.clearTimeout(timeoutId);
  }, [selectedSub]);

  // --- Stripe-based handlers that target selectedSub ---

  const targetStripeId = selectedSub?.stripeData?.id;
  const targetUserSubId = selectedSub?.id;
  const isSelectedSubCancelled = isCancelledSubscription(selectedSub);
  const isSelectedSubOneOff = isOneOffSubscription(selectedSub);
  const isSelectedSubPaused = isPausedSubscription(selectedSub);
  const selectedSubStripeStatus = getEffectiveSubscriptionStatus(selectedSub);
  
  const isFormulaSubscriptionEligible = selectedSub?.product_type === 'riise_formula' &&
    !isSelectedSubOneOff && !isSelectedSubCancelled && !isSelectedSubPaused &&
    selectedSubStripeStatus === 'active' && normalizeSubscriptionStatus(selectedSub?.status) === 'active';
  const { currentData: formulaEdit, isFetching: isFetchingFormula, refetch: refetchFormula } =
    useGetDashboardFormulaQuery({ lang, userSubscriptionId: selectedSub?.id }, {
      skip: !hasCustomerToken || !isFormulaSubscriptionEligible,
      refetchOnMountOrArgChange: true,
    });
  const canEditFormula = Boolean(isFormulaSubscriptionEligible && formulaEdit?.canEdit && !isFetchingFormula &&
    Number(formulaEdit.userSubscriptionId) === Number(selectedSub?.id) &&
    !selectedSub?.pendingFormulaUpdate && !selectedSub?.pendingPlanChange);

  const canSelectedSubUseRetentionCoupon =
    Boolean(targetStripeId) &&
    !isSelectedSubOneOff &&
    !isSelectedSubCancelled &&
    !isSelectedSubPaused &&
    selectedSubStripeStatus === 'active';
  const canSelectedSubRequestFormulaReview =
    Boolean(selectedSub) && !isSelectedSubOneOff && !isSelectedSubCancelled;
  const nextSupposedRenewalDate = useMemo(() => {
    return (
      parseDateValue(selectedSub?.stripeData?.upcoming_invoice?.next_payment_date) ||
      parseDateValue(selectedSub?.stripeData?.current_period_end) ||
      new Date()
    );
  }, [selectedSub]);
  const nextSupposedRenewalDateInput = useMemo(
    () => toDateInputValue(nextSupposedRenewalDate),
    [nextSupposedRenewalDate],
  );
  const pauseOptionDates = useMemo(() => {
    const cadenceDays = getCadenceDaysForSubscription(selectedSub);
    const locale = getBrowserLocale(selectedLang);

    if (!cadenceDays) {
      return { skip_1: '', skip_2: '' };
    }

    return {
      skip_1: formatDisplayDate(addDays(nextSupposedRenewalDate, cadenceDays), locale),
      skip_2: formatDisplayDate(addDays(nextSupposedRenewalDate, cadenceDays * 2), locale),
    };
  }, [nextSupposedRenewalDate, selectedLang, selectedSub]);

  async function handleCancelSubscription(cancelData) {
    if (!targetStripeId || isSelectedSubCancelled || isSelectedSubOneOff) {
      toast.error(t('CustomerSubs_NoActiveSubToCancel'));
      return;
    }
    try {
      const payload = {
        subId: targetStripeId,
        userSubscriptionId: targetUserSubId,
        reason: cancelData?.reason,
        note: cancelData?.extraNote,
        tags: cancelData?.tags,
      };
      const result = await cancelSubscriptionMutation(payload).unwrap();
      if (result?.canceledSubscription) {
        toast.success(t('CustomerSubs_Cancel_Success'));
        setSelectedSub(null);
        await refetchUserSubscription();
      } else {
        toast.error(t('CustomerSubs_Cancel_Error'));
      }
    } catch (e) {
      toast.error(e?.data?.message || e?.message || t('CustomerSubs_Error_Generic'));
    }
  }

  async function handleConfirmPause({ option, date }, successKey = 'CustomerSubs_Pause_Success') {
    if (!hasCustomerToken) {
      toast.error(t('CustomerSubs_LoginRequiredForPause'));
      return;
    }
    if (!targetStripeId || isSelectedSubCancelled || isSelectedSubOneOff) {
      toast.error(t('CustomerSubs_NoActiveSubToPause'));
      return;
    }

    const cadenceDays = getCadenceDaysForSubscription(selectedSub);
    let resolvedResumeDate = null;

    if (option === 'skip_1') {
      resolvedResumeDate = cadenceDays
        ? toDateInputValue(addDays(nextSupposedRenewalDate, cadenceDays))
        : nextSupposedRenewalDateInput;
    } else if (option === 'skip_2') {
      resolvedResumeDate = cadenceDays
        ? toDateInputValue(addDays(nextSupposedRenewalDate, cadenceDays * 2))
        : nextSupposedRenewalDateInput;
    } else if (option === 'specific_date' || option === 'date') {
      resolvedResumeDate = date || null;
    }

    if (!resolvedResumeDate) {
      toast.error(t('CustomerSubs_Pause_Error'));
      return;
    }

    const resolvedResumeAt = parseDateValue(resolvedResumeDate);
    if (
      (option === 'specific_date' || option === 'date') &&
      resolvedResumeAt &&
      resolvedResumeAt < nextSupposedRenewalDate
    ) {
      toast.error(t('CustomerSubs_Pause_Error'));
      return;
    }

    try {
      const payload = {
        mode: 'UNTIL_DATE',
        resumeDate: resolvedResumeDate,
        stripeSubscriptionId: targetStripeId,
        userSubscriptionId: targetUserSubId,
      };
      const data = await pauseSubscriptionMutation(payload).unwrap();
      if (data?.success) {
        toast.success(t(successKey));
        setShowPauseModal(false);
        setShowDeliveryDateModal(false);
        await refetchUserSubscription();
      } else {
        toast.error(t('CustomerSubs_Pause_Error'));
      }
    } catch (e) {
      toast.error(e?.data?.message || e?.message || t('CustomerSubs_Error_Generic'));
    }
  }

  const handleConfirmDeliveryDate = (payload) => {
    return handleConfirmPause(payload, 'CustomerSubs_DeliveryDate_Success');
  };

  async function handleResumeSubscription({ mode = 'IMMEDIATE', resumeDate = null } = {}) {
    if (!targetStripeId) {
      toast.error(t('CustomerSubs_NoSubToResume'));
      return;
    }
    try {
      const data = await resumeSubscriptionMutation({
        stripeSubscriptionId: targetStripeId,
        userSubscriptionId: targetUserSubId,
        mode,
        resumeDate: mode === 'ON_DATE' ? resumeDate : null,
      }).unwrap();
      if (data?.success) {
        toast.success(
          data?.resumedImmediately
            ? t('CustomerSubs_Resume_Success')
            : t('CustomerSubs_Resume_Scheduled_Success'),
        );
        setShowResumeConfirm(false);
        await refetchUserSubscription();
      } else {
        toast.error(t('CustomerSubs_Resume_Error'));
      }
    } catch (e) {
      toast.error(e?.data?.message || e?.message || t('CustomerSubs_Error_Generic'));
    }
  }

  const handleRemoveItem = useCallback(
    async (itemId) => {
      try {
        await removeSubscriptionItemMutation(itemId).unwrap();
        toast.success(t('CustomerSubs_ManagePanel_RemoveItem_Success'));
        await refetchUserSubscription();
        return true;
      } catch (e) {
        toast.error(e?.data?.message || t('CustomerSubs_ManagePanel_RemoveItem_Error'));
        return false;
      }
    },
    [removeSubscriptionItemMutation, refetchUserSubscription, t],
  );

  const lastSubscription = visibleSubscriptionsList?.[visibleSubscriptionsList.length - 1];
  const lastStatus = lastSubscription?.status;
  const lastDuration = lastSubscription?.duration;
  const lastProductType = (lastSubscription?.product_type ?? '').toLowerCase();
  const lastDurationNum =
    typeof lastDuration === 'number' ? lastDuration : parseInt(String(lastDuration ?? ''), 10);
  const isOneOffOrCancelled =
    lastQuizId &&
    lastProductType === 'riise_formula' &&
    (lastStatus === 'cancelled' ||
      lastStatus === 'canceled' ||
      lastDurationNum === 30 ||
      lastDurationNum === 90);

  const handleManageSub = (sub) => {
    setIsFormulaEditorOpen(false);
    setSelectedSub((prev) => (prev?.id === sub.id ? null : sub));
  };

  return (
    <DashboardWrapper title={t(Subscription)}>
      <div className="col-xl-8 col-lg-8 user-inner space-y-12 !t-gap-4 t-max-w-full t-overflow-x-hidden">
        <div className="t-flex t-flex-col">
          <h4 className="t-font-bold">{t('CustomerSubs_Status')}</h4>
          {isLoading ? (
            <p>{t('CustomerSubs_Loading')}</p>
          ) : (
            <>
              <SubscriptionsStatusTable
                subscriptionsList={visibleSubscriptionsList}
                selectedLang={selectedLang}
                onManage={handleManageSub}
                selectedSubId={selectedSub?.id}
              />
              {selectedSub && (
                <div ref={managePanelRef} className="t-scroll-mt-24">
                  <ManageSubscriptionPanel
                    subscription={selectedSub}
                    onPause={() => setShowPauseModal(true)}
                    onChangeDeliveryDate={() => setShowDeliveryDateModal(true)}
                    onResume={() => setShowResumeConfirm(true)}
                    onCancel={() => setShowCancelModal(true)}
                    onEditFormula={canEditFormula ? () => setIsFormulaEditorOpen(true) : undefined}
                    isFormulaLoading={isFormulaSubscriptionEligible && isFetchingFormula}
                    onChangePlan={() => setShowChangePlanModal(true)}
                    onRemoveItem={handleRemoveItem}
                    onClose={() => setSelectedSub(null)}
                    isPausing={isPausing}
                    isChangingDeliveryDate={isPausing}
                    isResuming={isResuming}
                    isCancelling={isCancelling}
                    isRemoving={isRemoving}
                  />
                </div>
              )}
            </>
          )}
          {isOneOffOrCancelled && (
            <div className="t-mt-6 t-rounded-xl t-border t-border-g200 t-bg-surface t-p-4 md:t-p-5">
              <div className="t-flex t-flex-col t-gap-3">
                <div>
                  <h5 className="t-m-0 t-font-bold t-text-navy">{t('CustomerSubs_Riise_Title')}</h5>
                  <p className="t-m-0 t-mt-1 t-text-sm t-text-g500">{t('CustomerSubs_Riise_Body')}</p>
                </div>
                <div className="t-flex t-flex-col md:t-flex-row md:t-items-center t-gap-3">
                  <button
                    type="button"
                    className="btn btn-custom-secondary !t-bg-primary !t-text-secondary fw-bold lts1 px-3 !t-rounded-lg"
                    onClick={() => {
                      navigate(`/product-suggestion?quizId=${lastQuizId}`, {
                        state: { returningUser: true },
                      });
                    }}
                  >
                    {t('CustomerSubs_UsePreviousFormula')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-custom-secondary !t-bg-primary !t-text-secondary fw-bold lts1 px-3 !t-rounded-lg"
                    onClick={() =>
                      navigate('/quiz', {
                        state: { prefilledEmail: user?.email, prefilledName: user?.name },
                      })
                    }
                  >
                    {t(BuyWithNewQuizBtnText)}
                  </button>
                </div>
              </div>
            </div>
          )}
          {lang === 'de' && (
            <div className="t-mt-6">
              <ExplainerVideo
                src={SUBS_VIDEO_DE}
                eyebrow={t('CustomerSubs_Video_Eyebrow')}
                title={t('CustomerSubs_Video_Title')}
              />
            </div>
          )}
        </div>
      </div>

      <PauseSubscriptionModal
        open={showPauseModal}
        onClose={() => setShowPauseModal(false)}
        onConfirm={handleConfirmPause}
        isSubmitting={isPausing}
        minDate={nextSupposedRenewalDateInput}
        optionDates={pauseOptionDates}
      />

      <PauseSubscriptionModal
        open={showDeliveryDateModal}
        onClose={() => setShowDeliveryDateModal(false)}
        onConfirm={handleConfirmDeliveryDate}
        isSubmitting={isPausing}
        minDate={nextSupposedRenewalDateInput}
        optionDates={pauseOptionDates}
        mode="deliveryDate"
      />

      <ResumeSubscriptionModal
        open={showResumeConfirm}
        onClose={() => setShowResumeConfirm(false)}
        onConfirm={handleResumeSubscription}
        isSubmitting={isResuming}
        selectedLang={selectedLang}
        subscriptionAmount={
          selectedSub?.stripeData?.upcoming_invoice?.amount_due ?? selectedSub?.stripeData?.amount
        }
      />

      <CancelSubscriptionModal
        open={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        stripeSubscriptionId={targetStripeId}
        userSubscriptionId={targetUserSubId}
        productType={selectedSub?.product_type}
        lang={lang}
        subscriptionAmount={
          selectedSub?.stripeData?.upcoming_invoice?.amount_due ?? selectedSub?.stripeData?.amount
        }
        subscriptionDuration={selectedSub?.duration}
        nextInvoiceDate={selectedSub?.stripeData?.upcoming_invoice?.next_payment_date}
        minPauseDate={nextSupposedRenewalDateInput}
        optionDates={pauseOptionDates}
        onConfirmCancel={handleCancelSubscription}
        onConfirmPause={handleConfirmPause}
        refetchUserSubscription={refetchUserSubscription}
        isSubmitting={isCancelling}
        canApplyCoupon={canSelectedSubUseRetentionCoupon}
        canRequestFormulaReview={canSelectedSubRequestFormulaReview}
      />

      {isFormulaEditorOpen && formulaEdit && (
        <DashboardFormulaEditor
          startAtEdit
          formula={formulaEdit}
          lang={lang}
          name={user?.name || ''}
          handleClose={() => setIsFormulaEditorOpen(false)}
          handleSaved={async () => {
            setIsFormulaEditorOpen(false);
            await Promise.all([refetchUserSubscription(), refetchFormula()]);
          }}
        />
      )}

      <ChangePlanModal
        open={showChangePlanModal}
        onClose={() => setShowChangePlanModal(false)}
        userSubscriptionId={selectedSub?.id}
        selectedLang={selectedLang}
        onSuccess={() => refetchUserSubscription?.()}
      />
    </DashboardWrapper>
  );
}

export default CustomerSubscriptions;
